import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatRef, parseRef, streetLine, type PortfolioScope } from '@promanage/core';
import { Button } from '@promanage/ui';
import { ColumnPicker } from '../features/table/ColumnPicker';
import { DataTable } from '../features/table/DataTable';
import { EmptyState, FilterChips, ListShell } from '../features/table/ListShell';
import { PropertiesNav } from '../features/property/PropertiesNav';
import { compareRows, type SortState } from '../features/table/columns';
import { storedColumnState, useColumnState } from '../features/table/useColumnState';
import {
  DEFAULT_PROPERTY_SORT,
  propertyColumns,
  type PropertyColumnId,
} from '../features/property/propertyColumns';
import { todayIso } from '../features/property/today';
import type { Property } from '../features/property/types';
import { trpc } from '../lib/trpc';
import { portfolioScopeFor, usePortfolioScope } from '../features/portfolio/scope';
import { resolveSession } from '../features/auth/session';
import { propertyListExtras, type PropertyListExtra } from '../features/property/propertyListData';
import {
  isLocatedFilter,
  locatedFilterLabel,
  matchesLocated,
  type LocatedFilter,
} from '../features/property/located';
import { formatArea, parseArea } from '../features/property/area';
import { useAreaProperties } from '../features/property/useAreaProperties';
import { WorkspaceMenu, useWorkspace } from '../features/workspaces/WorkspaceMenu';
import { workspaceFromHref } from '../features/workspaces/href';
import { workspaceChoice, workspaceTable, workspaceText } from '../features/workspaces/state';

/** The one filter that arrives as a LINK rather than as a chip — see `features/property/located`. */
interface PropertyListSearch {
  located?: LocatedFilter;
  /**
   * The properties in one suburb or grid — `?area=suburb:1234`, `?area=grid:<uuid>`. Arrives as a
   * link from the map's panel, like `located`, and is kept as the string it arrived as: the shape
   * is checked here and the meaning is `parseArea`'s, in a module the eager route tree can afford.
   */
  area?: string;
}

export const Route = createFileRoute('/properties/')({
  /*
   * SHAPE-CHECKED AND OTHERWISE FORGIVING, the same call the transactions page makes about
   * `account`. This lands here from the map's "not on the map" count, and it is also a URL people
   * edit and bookmark — so a value that is not a filter drops out and the list renders unfiltered,
   * which is a better answer than a router error page for a link that has gone stale.
   */
  validateSearch: (search: Record<string, unknown>): PropertyListSearch => {
    const area = parseArea(search.area);
    return {
      ...(typeof search.located === 'string' && isLocatedFilter(search.located)
        ? { located: search.located }
        : {}),
      // Written back in its one canonical spelling, as the map does with a staff ref.
      ...(area ? { area: formatArea(area) } : {}),
    };
  },
  /*
   * PREFETCH THE LIST THE PAGE IS ABOUT TO ASK FOR — the same key, or this is a wasted request.
   *
   * The query is no longer `{ scope }` alone: `include` names the optional groups the visible
   * columns need, so the key depends on a column preference that lives in `localStorage`. A
   * loader that prefetched the bare scope would fill a cache slot the component never reads,
   * and the page would still wait on the network with a prefetch to pay for. So the loader
   * reads the stored columns through the very function the hook initialises from
   * (`storedColumnState`) and derives `include` the same way the component does.
   *
   * `sort` is the default here on purpose: it is what the component starts with, and the two
   * sorts that pull in a hidden group are ones a person chooses after the page is up.
   */
  loader: async ({ context, location }) => {
    // A saved view is about to replace the filters, the columns and possibly the scope, and the
    // component holds its query until that lands. Prefetching the default would be a round trip
    // for an answer nobody reads.
    if (workspaceFromHref(location.href) !== null) return;

    const session = await resolveSession(context.queryClient);
    const scope = portfolioScopeFor(session);
    const columns = storedColumnState(STORAGE_KEY, propertyColumns(todayIso()));

    await context.queryClient.prefetchQuery(
      propertyListOptions(scope, propertyListExtras(columns.order, DEFAULT_PROPERTY_SORT.column)),
    );
  },
  component: PropertiesPage,
});

const STORAGE_KEY = 'promanage:property-columns';

const propertyListOptions = (scope: PortfolioScope, include: PropertyListExtra[]) =>
  trpc.property.list.queryOptions({ scope, include }, { staleTime: 10_000 });

/** One identity, so the pending state does not re-sort the table on every render. */
const NO_PROPERTIES: Property[] = [];

type OccupancyFilter = 'all' | 'let' | 'vacant' | 'noncompliant';

const FILTERS: { value: OccupancyFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'let', label: 'Let' },
  { value: 'vacant', label: 'Vacant' },
  { value: 'noncompliant', label: 'Not compliant' },
];

/**
 * The properties list.
 *
 * Every row answers the three questions asked of a property fifty times a day: where is it,
 * who owns it, and is anybody in it. The fourth — is it compliant — is the column that earns
 * its place in New Zealand specifically, and it sorts worst-first.
 *
 * Filtering happens in the browser rather than round-tripping. An agency has hundreds of
 * properties, not millions, and a filter that waits for the network is a filter people stop
 * using. When that stops being true it becomes a server query, and this comment is the record
 * of why it was not one to begin with.
 */
function PropertiesPage() {
  const navigate = useNavigate();
  const { located, area: areaParam } = Route.useSearch();
  const today = todayIso();
  const { scope, setScope, canToggle } = usePortfolioScope();
  // `scope` is part of the query key, so switching refetches and each view stays cached in its
  // own slot. The server clamps it regardless of what the browser has stored.

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<OccupancyFilter>('all');
  const [sort, setSort] = useState<SortState<PropertyColumnId>>(DEFAULT_PROPERTY_SORT);

  // Memoised on `today` so the array identity is stable across renders — the column state hook
  // and the table both key off it.
  const catalogue = useMemo(() => propertyColumns(today), [today]);
  const columns = useColumnState(STORAGE_KEY, catalogue);
  const workspace = useWorkspace({
    route: '/properties',
    state: { filters: { query, filter, scope }, columns: columns.state.order, sort },
    apply(state) {
      const table = workspaceTable(state, catalogue, DEFAULT_PROPERTY_SORT);
      setQuery(workspaceText(state, 'query'));
      setFilter(
        workspaceChoice(
          state,
          'filter',
          FILTERS.map((option) => option.value),
          'all',
        ),
      );
      if (canToggle) setScope(workspaceChoice(state, 'scope', ['own', 'agency'], 'own'));
      columns.setState(table.columns);
      setSort(table.sort);
    },
  });

  // The loader builds this same key from the stored columns — see the note on the route above.
  const include = propertyListExtras(columns.state.order, sort.column);
  const list = useQuery({
    meta: { errorTitle: 'Could not load the properties' },
    ...propertyListOptions(scope, include),
    enabled: !workspace.isRestoring,
  });
  const properties = workspace.isRestoring ? NO_PROPERTIES : (list.data ?? NO_PROPERTIES);

  /*
   * WHICH PROPERTIES ARE IN THE AREA is the map's answer, fetched the map's way — see
   * `useAreaProperties`. Until it is known the list waits rather than flashing every property and
   * then dropping most of them; if it cannot be known, the list shows everything and says why.
   */
  const area = useMemo(() => parseArea(areaParam), [areaParam]);
  const inArea = useAreaProperties(area, scope);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    // The link-in filter goes FIRST: when it is set it is the reason this page was opened at all,
    // and the chips and the search box then narrow within it as they would on the full list.
    return properties
      .filter((property) => matchesLocated(property, located))
      .filter((property) => inArea.refSeqs === null || inArea.refSeqs.has(property.refSeq))
      .filter((property) => {
        switch (filter) {
          case 'let':
            return property.currentTenancy !== null;
          case 'vacant':
            return property.currentTenancy === null;
          case 'noncompliant':
            return property.healthyHomes === 'not_compliant';
          default:
            return true;
        }
      })
      .filter((property) => {
        if (!q) return true;

        // A ref typed into search resolves directly, however it was typed — and a TENANCY ref
        // finds the property it belongs to, because that is the record you can actually open.
        const asRef = parseRef(q);
        if (asRef?.entity === 'property') return asRef.sequence === property.refSeq;
        if (asRef?.entity === 'tenancy') {
          return property.currentTenancy?.refSeq === asRef.sequence;
        }

        return [
          streetLine(property),
          formatRef('property', property.refSeq),
          property.suburb,
          property.city,
          property.postcode,
          ...property.owners.map((o) => o.name),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => compareRows(a, b, sort, catalogue));
  }, [properties, query, filter, located, inArea.refSeqs, sort, catalogue]);

  /*
   * THE COLUMN THAT EXPLAINS THE FILTER, shown whether or not the operator keeps it.
   *
   * "On the map" is `defaultVisible: false` — a data-quality column most people never need. But
   * arriving here from the map's count means the reason these twelve rows are on screen is a
   * column that would be hidden, so the list would answer "which twelve?" and decline to answer
   * "why these?". Derived rather than pushed through `columns.setState`, so following a link does
   * not quietly rearrange the table the operator gets back tomorrow.
   *
   * It costs nothing on the wire: `located` maps to no `include` group, because `geocodeStatus`
   * rides on the base row — see `propertyListExtras`.
   */
  const visible = useMemo(() => {
    if (located === undefined || columns.visible.some((column) => column.id === 'located')) {
      return columns.visible;
    }
    const column = catalogue.find((c) => c.id === 'located');
    return column ? [...columns.visible, column] : columns.visible;
  }, [columns.visible, catalogue, located]);

  /* The link said there was something to fix and there is not — see the empty state below. */
  const nothingToFix = located !== undefined && query === '' && filter === 'all';
  /* The area itself holds nothing — not "no properties yet", which would be alarming and wrong. */
  const emptyArea = area !== null && inArea.status === 'ready' && query === '' && filter === 'all';

  const total = properties.length;
  const vacant = properties.filter((p) => p.currentTenancy === null).length;
  const summary = list.isPending
    ? 'Loading…'
    : rows.length === total
      ? `${total} under management · ${vacant} vacant`
      : `Showing ${rows.length} of ${total} properties`;

  return (
    <ListShell
      title="Properties"
      subtitle={summary}
      actions={
        <>
          <WorkspaceMenu workspace={workspace} />
          <ColumnPicker
            columns={catalogue}
            state={columns.state}
            onChange={columns.setState}
            onReset={columns.reset}
          />
          <Link to="/properties/new">
            <Button size="sm">
              <span aria-hidden="true">+</span> Add property
            </Button>
          </Link>
        </>
      }
      search={query}
      onSearchChange={setQuery}
      nav={<PropertiesNav />}
      searchPlaceholder="Search address, suburb, ref (PR000001) or owner"
      searchLabel="Search properties"
      filters={
        <FilterChips
          label="Filter by occupancy"
          value={filter}
          options={FILTERS}
          onChange={setFilter}
        />
      }
      trailing={
        area !== null ? (
          /*
           * The same job as the `located` chip below: the list arrived narrowed, and this is the
           * reason. It names the place rather than the mechanism — "in Hillcrest", not "area".
           */
          <div className="flex items-center gap-2 rounded-pill bg-accent-soft py-1 pr-1 pl-3 text-[0.75rem] font-semibold text-accent">
            <span>
              {inArea.status === 'unavailable'
                ? 'Could not work out that area — showing every property'
                : inArea.status === 'pending'
                  ? 'Finding the properties in that area…'
                  : inArea.name === null
                    ? `That ${area.kind} no longer exists`
                    : area.kind === 'grid'
                      ? `Properties in the ${inArea.name} grid`
                      : `Properties in ${inArea.name}`}
            </span>
            <Link to="/properties/map" search={{ view: area.kind === 'grid' ? 'grids' : 'suburbs', ...(area.kind === 'grid' ? { grid: area.id } : { suburb: area.id }) }}>
              <Button size="sm" variant="secondary">
                Map
              </Button>
            </Link>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void navigate({ to: '/properties', search: {} })}
            >
              Clear
            </Button>
          </div>
        ) : located === undefined ? undefined : (
          /*
           * SAYS WHY, not just THAT. "Showing 12 of 400" in the subtitle is the count; this is the
           * reason, and without it a list that arrived pre-filtered from another page reads as a
           * list that has mislaid three hundred and eighty-eight properties.
           */
          <div className="flex items-center gap-2 rounded-pill bg-accent-soft py-1 pr-1 pl-3 text-[0.75rem] font-semibold text-accent">
            <span>Properties {locatedFilterLabel(located)}</span>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void navigate({ to: '/properties', search: {} })}
            >
              Clear
            </Button>
          </div>
        )
      }
      isPending={list.isPending || workspace.isRestoring || inArea.status === 'pending'}
      error={list.error}
      onRetry={() => void list.refetch()}
      isEmpty={rows.length === 0}
      empty={
        <EmptyState
          /*
           * AN EMPTY LINK-IN RESULT IS THE GOOD OUTCOME, and "No properties yet" would report the
           * best possible news as the worst. Somebody followed a count that said twelve addresses
           * needed fixing; if none are left, the sentence they need is that the work is done.
           */
          title={
            emptyArea
              ? 'No properties in that area'
              : nothingToFix
              ? 'Every property is on the map'
              : query
                ? `Nothing matches “${query}”`
                : 'No properties yet'
          }
          action={
            nothingToFix || emptyArea ? (
              <Link to="/properties" search={{}}>
                <Button size="sm">All properties</Button>
              </Link>
            ) : (
              <div className="flex gap-2">
                <Link to="/owners">
                  <Button size="sm" variant="secondary">
                    Owners
                  </Button>
                </Link>
                <Link to="/properties/new">
                  <Button size="sm">Add property</Button>
                </Link>
              </div>
            )
          }
        >
          {emptyArea
            ? 'A property belongs to an area by its pin on the map, so one without a pin is in none.'
            : nothingToFix
            ? `No property is ${locatedFilterLabel(located ?? 'unmapped')}.`
            : query
              ? 'Try a different address, suburb, ref or owner name.'
              : 'A property needs at least one owner — there has to be somebody for the rent to belong to — so add the owner first.'}
        </EmptyState>
      }
    >
      <DataTable
        rows={rows}
        columns={visible}
        sort={sort}
        onSortChange={setSort}
        rowKey={(property) => property.id}
        onOpen={(property) =>
          void navigate({
            to: '/properties/$ref',
            params: { ref: formatRef('property', property.refSeq) },
          })
        }
        caption={`Properties, one row per property. ${summary}.`}
        /*
         * GROWS WITH THE COLUMN COUNT rather than being a constant. The widths in the catalogue
         * are percentages of a `table-fixed` layout, so at twenty columns a fixed 58rem floor
         * gives each of them about forty pixels and every cell truncates to nothing. Nine rem a
         * column is roughly what a date plus its sub-line needs; below the default eight columns
         * the original floor still wins, so nothing about the unconfigured page changes.
         */
        minWidth={`${Math.max(58, visible.length * 9)}rem`}
      />
    </ListShell>
  );
}
